import { BILLING_STATUS } from '../vocabulary.js';

/** @typedef {import('../../shell/types/billing.types.js').BillingItemApiRequest} BillingItemApiRequest */
/** @typedef {import('../../shell/types/billing.types.js').MarkInvoicedApiRequest} MarkInvoicedApiRequest */
/** @typedef {import('../../shell/types/billing.types.js').PlatformCostApiRequest} PlatformCostApiRequest */
/** @typedef {import('../../shell/types/billing.types.js').OneOffIncomeApiRequest} OneOffIncomeApiRequest */
/** @typedef {import('../../shell/types/billing.types.js').InvoiceCostApiRequest} InvoiceCostApiRequest */

const MAX_INTEGER_DIGITS = 8;
const MAX_DECIMALS = 2;
/** Longest interval text that still fits the backend's 32-bit integer. */
const MAX_INTERVAL_DIGITS = 9;
/** Largest interval in months the backend accepts (ten years). */
const MAX_INTERVAL_MONTHS = 120;

const MAX_TEXT_LENGTH = 255;
export const MAX_INVOICE_NUMBER_LENGTH = 64;
const MAX_URL_LENGTH = 1024;
const HTTPS_URL = /^https:\/\/\S+$/;

const euroFormat = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' });

/**
 * Status label and badge class per status. Unknown statuses fall back to the grey badge.
 */
const STATUS_PRESENTATION = Object.freeze({
  [BILLING_STATUS.OVERDUE]: { label: 'Overdue', badgeClass: 'badge-red' },
  [BILLING_STATUS.DUE_SOON]: { label: 'Due soon', badgeClass: 'badge-amber' },
  [BILLING_STATUS.OK]: { label: 'Up to date', badgeClass: 'badge-green' },
  [BILLING_STATUS.INACTIVE]: { label: 'Inactive', badgeClass: 'badge-gray' },
});

/**
 * Normalizes typed money to a canonical two-decimal string using string logic only, so no
 * floating point value is ever involved. Either `,` or `.` is the one decimal separator;
 * thousands separators and spaces inside the number are not supported and rejected, because
 * `1.234` would be ambiguous. Surrounding whitespace is ignored.
 *
 * @param {string|null|undefined} text
 * @param {{ required?: boolean }} [options] `required` defaults to true
 * @returns {{ value: string|null, error: string|null }} `value` is null for an empty optional input
 */
export function normalizeMoney(text, { required = true } = {}) {
  const input = (text ?? '').trim();
  if (input === '') {
    return required
      ? { value: null, error: 'Enter an amount.' }
      : { value: null, error: null };
  }
  if (input.startsWith('-')) {
    return { value: null, error: 'The amount cannot be negative.' };
  }
  const match = /^(\d*)(?:[.,](\d*))?$/.exec(input);
  if (!match || (match[1] === '' && !match[2])) {
    return { value: null, error: 'Enter a valid amount, for example 12,50.' };
  }
  const integerPart = match[1].replace(/^0+(?=\d)/, '') || '0';
  const decimals = match[2] ?? '';
  if (integerPart.length > MAX_INTEGER_DIGITS) {
    return { value: null, error: `The amount has at most ${MAX_INTEGER_DIGITS} digits before the decimal separator.` };
  }
  if (decimals.length > MAX_DECIMALS) {
    return { value: null, error: `The amount has at most ${MAX_DECIMALS} decimals.` };
  }
  return { value: `${integerPart}.${decimals.padEnd(MAX_DECIMALS, '0')}`, error: null };
}

/**
 * A response money value in integer cents.
 *
 * @param {number} value
 * @returns {number}
 */
export function toCents(value) {
  return Math.round(value * 100);
}

/**
 * Sum of response money values, added as integer cents.
 *
 * @param {Array<number|null|undefined>} values null and undefined count as zero
 * @returns {number} the sum in euros with at most two decimals
 */
export function sumMoney(values) {
  const cents = values.reduce((total, value) => total + (value == null ? 0 : toCents(value)), 0);
  return cents / 100;
}

const byText = (a, b) => a.localeCompare(b, 'nl-BE', { sensitivity: 'base' });
/** By name (case and accent insensitive, Dutch (Belgium) collation), the slug breaking ties between equal names. */
const byClientName = (a, b) => byText(a.name, b.name) || a.slug.localeCompare(b.slug);

/**
 * The clients to choose from in a client selector, in the order of the grouped billing table.
 *
 * @param {Array<{ slug: string, name: string }>} clients
 * @returns {Array<{ slug: string, name: string }>} only the slug and name of each client
 */
export function clientOptions(clients) {
  return clients.map(({ slug, name }) => ({ slug, name })).sort(byClientName);
}

/**
 * Groups billing items per client for the overview table. Clients are sorted by name (see `clientOptions`);
 * the items of a client keep the order they came in (the API lists them by `paidUntil`).
 *
 * @param {Array<{ clientSlug: string, clientName: string }>} items
 * @returns {Array<{ slug: string, name: string, items: Array<object> }>}
 */
export function groupItemsByClient(items) {
  const groups = new Map();
  for (const item of items) {
    if (!groups.has(item.clientSlug)) {
      groups.set(item.clientSlug, { slug: item.clientSlug, name: item.clientName, items: [] });
    }
    groups.get(item.clientSlug).items.push(item);
  }
  return [...groups.values()].sort(byClientName);
}

/**
 * Explains an item's low-margin warning. The server judges the last invoice, or the item's own price and cost
 * before its first invoice.
 *
 * @param {{ lastInvoice: object | null }} item
 * @returns {string}
 */
export function lowMarginHint(item) {
  return item.lastInvoice
    ? 'Low margin: the cost of the last invoice is at least 90% of its amount.'
    : 'Low margin: the cost is at least 90% of the price.';
}

/**
 * Groups platform costs per supplier for the costs table. Suppliers are sorted by name (case and accent
 * insensitive, Dutch (Belgium) collation, the exact name breaks ties); the costs of a supplier keep the order
 * they came in (the API lists them newest period first). The API stores supplier names trimmed, so costs of
 * one supplier share its exact name.
 *
 * @param {Array<{ supplier: string, amount: number }>} costs
 * @returns {Array<{ supplier: string, costs: Array<object>, total: number }>} `total` is the cent-exact sum of
 *   the supplier's amounts
 */
export function groupCostsBySupplier(costs) {
  const groups = new Map();
  for (const cost of costs) {
    if (!groups.has(cost.supplier)) {
      groups.set(cost.supplier, { supplier: cost.supplier, costs: [] });
    }
    groups.get(cost.supplier).costs.push(cost);
  }
  return [...groups.values()]
    .map((group) => ({ ...group, total: sumMoney(group.costs.map((cost) => cost.amount)) }))
    .sort((a, b) => byText(a.supplier, b.supplier) || a.supplier.localeCompare(b.supplier));
}

/**
 * @param {number|null|undefined} value
 * @returns {string} e.g. `€ 12,50`, or an empty string for null and undefined
 */
export function formatEuro(value) {
  return value == null ? '' : euroFormat.format(value);
}

/**
 * @param {string|null|undefined} isoDate yyyy-MM-dd
 * @returns {string} dd/mm/yyyy, or an empty string when the input is not a yyyy-MM-dd date
 */
export function formatDate(isoDate) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate ?? '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

/**
 * @param {string} status a `BILLING_STATUS` value
 * @returns {{ label: string, badgeClass: string }}
 */
export function statusPresentation(status) {
  return STATUS_PRESENTATION[status] ?? { label: String(status), badgeClass: 'badge-gray' };
}

/**
 * @param {string|null|undefined} value
 * @returns {boolean} true for a real calendar date written as yyyy-MM-dd
 */
export function isIsoDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function requiredDate(value, name, errors) {
  if (!isIsoDate(value)) errors[name] = 'Enter a valid date.';
}

function blankToNull(text) {
  const trimmed = (text ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

function moneyField(text, name, errors, required) {
  const { value, error } = normalizeMoney(text, { required });
  if (error) errors[name] = error;
  return value;
}

/**
 * Validates the billing item form the way the backend does and builds the request.
 *
 * @param {{ label?: string, price?: string, cost?: string, supplier?: string, intervalMonths?: string,
 *           paidUntil?: string, active?: boolean, notes?: string }} form raw input texts
 * @returns {{ valid: boolean, errors: Record<string, string>, request: BillingItemApiRequest|null }}
 */
export function validateBillingItemForm(form) {
  const errors = {};
  const label = (form.label ?? '').trim();
  if (label === '') errors.label = 'Enter a label.';
  else if (label.length > MAX_TEXT_LENGTH) errors.label = `Use at most ${MAX_TEXT_LENGTH} characters.`;
  const supplier = blankToNull(form.supplier);
  if (supplier !== null && supplier.length > MAX_TEXT_LENGTH) errors.supplier = `Use at most ${MAX_TEXT_LENGTH} characters.`;
  const price = moneyField(form.price, 'price', errors, true);
  const cost = moneyField(form.cost, 'cost', errors, false);
  const intervalText = (form.intervalMonths ?? '').trim();
  let intervalMonths = null;
  if (intervalText !== '') {
    if (!/^\d+$/.test(intervalText) || intervalText.length > MAX_INTERVAL_DIGITS || Number(intervalText) < 1 || Number(intervalText) > MAX_INTERVAL_MONTHS) {
      errors.intervalMonths = `The interval is a whole number of months, from 1 to ${MAX_INTERVAL_MONTHS}.`;
    } else {
      intervalMonths = Number(intervalText);
    }
  }
  requiredDate(form.paidUntil, 'paidUntil', errors);

  if (Object.keys(errors).length > 0) return { valid: false, errors, request: null };
  return {
    valid: true,
    errors,
    request: {
      label,
      price,
      cost,
      supplier,
      intervalMonths,
      paidUntil: form.paidUntil,
      active: Boolean(form.active),
      notes: blankToNull(form.notes),
    },
  };
}

/**
 * Validates the mark-invoiced form the way the backend does and builds the request. When the item's
 * current `paidUntil` is given, `periodTo` must lie after it (the invoice period starts there).
 *
 * @param {{ invoiceNumber?: string, invoicedOn?: string, periodTo?: string, amount?: string, cost?: string }} form
 * @param {string} [paidUntil] the item's current paidUntil, yyyy-MM-dd
 * @returns {{ valid: boolean, errors: Record<string, string>, request: MarkInvoicedApiRequest|null }}
 */
export function validateMarkInvoicedForm(form, paidUntil) {
  const errors = {};
  const invoiceNumber = (form.invoiceNumber ?? '').trim();
  if (invoiceNumber === '') errors.invoiceNumber = 'Enter an invoice number.';
  else if (invoiceNumber.length > MAX_INVOICE_NUMBER_LENGTH) errors.invoiceNumber = `Use at most ${MAX_INVOICE_NUMBER_LENGTH} characters.`;
  requiredDate(form.invoicedOn, 'invoicedOn', errors);
  requiredDate(form.periodTo, 'periodTo', errors);
  if (!errors.periodTo && paidUntil && form.periodTo <= paidUntil) {
    errors.periodTo = `The period must end after ${formatDate(paidUntil)}.`;
  }
  const amount = moneyField(form.amount, 'amount', errors, true);
  const cost = moneyField(form.cost, 'cost', errors, false);

  if (Object.keys(errors).length > 0) return { valid: false, errors, request: null };
  return {
    valid: true,
    errors,
    request: { invoiceNumber, invoicedOn: form.invoicedOn, periodTo: form.periodTo, amount, cost },
  };
}

/**
 * @param {Date} date
 * @returns {string} the date in the local time zone as yyyy-MM-dd (no shift through UTC)
 */
export function toLocalIsoDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * A response money value as the text of a money input, with the decimal comma.
 *
 * @param {number|null|undefined} value
 * @returns {string} e.g. `12,50`, or an empty string for null and undefined
 */
export function moneyInputValue(value) {
  return value == null ? '' : value.toFixed(2).replace('.', ',');
}

const MS_PER_DAY = 86400000;

/**
 * Whole days from one yyyy-MM-dd date to another, counted in UTC so no time zone or DST change shifts them.
 *
 * @param {string} from yyyy-MM-dd
 * @param {string} to yyyy-MM-dd
 * @returns {number}
 */
export function daysBetween(from, to) {
  const utc = (isoDate) => Date.UTC(Number(isoDate.slice(0, 4)), Number(isoDate.slice(5, 7)) - 1, Number(isoDate.slice(8, 10)));
  return (utc(to) - utc(from)) / MS_PER_DAY;
}

/**
 * The date `months` after a yyyy-MM-dd date, like the backend's `nextPeriodTo`: a month end stays a month
 * end (28 February 2027 plus 12 months is 29 February 2028), any other day is clamped to the target month.
 *
 * @param {string} isoDate yyyy-MM-dd
 * @param {number} months
 * @returns {string} yyyy-MM-dd
 */
export function addMonths(isoDate, months) {
  const [year, month, day] = isoDate.split('-').map(Number);
  const lastDay = (y, m) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const total = month - 1 + months;
  const [targetYear, targetMonth] = [year + Math.floor(total / 12), total % 12];
  const last = lastDay(targetYear, targetMonth);
  const targetDay = day === lastDay(year, month - 1) ? last : Math.min(day, last);
  return `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;
}

/**
 * A response money value prorated by day to a period that ends at `periodTo` instead of the
 * normal period end; both periods start at `paidUntil`. The value is first scaled by
 * `multiplier / divisor` (12 / intervalMonths turns a price into a yearly rate). Computed in
 * integer cents and rounded half up once.
 *
 * @param {number|null|undefined} value a response money value, such as `item.price` or `item.cost`
 * @param {string} paidUntil yyyy-MM-dd, where both periods start
 * @param {string} normalPeriodTo yyyy-MM-dd, the end of the period the scaled value pays for
 * @param {string} periodTo yyyy-MM-dd as typed, possibly not a date yet
 * @param {number} [multiplier]
 * @param {number} [divisor]
 * @returns {number|null} the prorated euros, or null when the value is null or the dates give no period
 */
export function proratedAmount(value, paidUntil, normalPeriodTo, periodTo, multiplier = 1, divisor = 1) {
  if (value == null || ![paidUntil, normalPeriodTo, periodTo].every(isIsoDate)) return null;
  const days = daysBetween(paidUntil, periodTo);
  const fullDays = daysBetween(paidUntil, normalPeriodTo);
  if (days <= 0 || fullDays <= 0) return null;
  const denominator = 2 * divisor * fullDays;
  return Math.floor((2 * toCents(value) * multiplier * days + divisor * fullDays) / denominator) / 100;
}

const INDEX_VALUE = /^(\d+)(?:[.,](\d{1,2}))?$/;
/** Fixed part of the indexation formula, in hundredths: new = base × (0,2 + 0,8 × S / S0). */
const FIXED_PART_HUNDREDTHS = 20n;
const INDEXED_PART_HUNDREDTHS = 80n;

/**
 * @param {string|null|undefined} text an index value as typed, `,` or `.` as decimal separator
 * @returns {bigint|null} the value in hundredths (`134,12` is `13412n`), or null when it has more
 *   than two decimals, is not a number or is not greater than zero
 */
function parseIndex(text) {
  const match = INDEX_VALUE.exec((text ?? '').trim());
  if (!match) return null;
  const hundredths = BigInt(match[1]) * 100n + BigInt((match[2] ?? '').padEnd(2, '0'));
  return hundredths > 0n ? hundredths : null;
}

/** @param {string} canonical a `normalizeMoney` value such as `300.00` @returns {bigint} */
export function canonicalToCents(canonical) {
  return BigInt(canonical.replace('.', ''));
}

/** @param {bigint} cents @returns {string} canonical money such as `305.06` */
function centsToCanonical(cents) {
  return `${cents / 100n}.${String(cents % 100n).padStart(2, '0')}`;
}

/** @param {bigint} hundredths @returns {string} e.g. `134,12`, with the decimal comma */
function indexText(hundredths) {
  return `${hundredths / 100n},${String(hundredths % 100n).padStart(2, '0')}`;
}

/**
 * The price indexed with the fixed-part formula `base × (0,2 + 0,8 × S / S0)`, in integer
 * arithmetic, rounded half up once and never below the base price.
 *
 * @param {string} basePrice money as typed (see `normalizeMoney`)
 * @param {string} baseIndex the base index `S0` as typed, at most two decimals
 * @param {string} newIndex the new index `S` as typed, at most two decimals
 * @returns {string|null} the canonical money string (`305.06`), or null when an input does not parse
 */
export function indexedPrice(basePrice, baseIndex, newIndex) {
  const { value } = normalizeMoney(basePrice);
  const s0 = parseIndex(baseIndex);
  const s = parseIndex(newIndex);
  if (value === null || s0 === null || s === null) return null;
  const baseCents = canonicalToCents(value);
  const n = baseCents * (FIXED_PART_HUNDREDTHS * s0 + INDEXED_PART_HUNDREDTHS * s);
  const cents = (2n * n + 100n * s0) / (200n * s0);
  return centsToCanonical(cents > baseCents ? cents : baseCents);
}

/**
 * The Dutch invoice line that explains `newPrice`, the non-null `indexedPrice(basePrice, baseIndex, newIndex)`.
 *
 * @returns {string}
 */
export function indexationLine(basePrice, baseIndex, newIndex, newPrice) {
  const [s0Text, sText] = [indexText(parseIndex(baseIndex)), indexText(parseIndex(newIndex))];
  const base = formatEuro(Number(normalizeMoney(basePrice).value));
  return 'Prijs geïndexeerd op de verjaardag van de overeenkomst volgens de gezondheidsindex (Statbel): '
    + `${base} × (0,2 + 0,8 × ${sText} / ${s0Text}) = ${formatEuro(Number(newPrice))}. `
    + `Basisindex: ${s0Text}. Nieuwe index: ${sText}.`;
}

/**
 * @param {string} periodFrom yyyy-MM-dd, the item's current paidUntil
 * @param {string} periodTo yyyy-MM-dd as typed, possibly not a date yet
 * @returns {string} e.g. `Period 01/02/2026 to 01/02/2027`; only the start while the end is no date
 */
export function periodText(periodFrom, periodTo) {
  const to = formatDate(periodTo);
  return to === '' ? `Period from ${formatDate(periodFrom)}` : `Period ${formatDate(periodFrom)} to ${to}`;
}

/**
 * How the nav badge shows the billing summary: the number of items that need attention, red when
 * anything is overdue, amber when items are only due soon, and hidden at zero.
 *
 * @param {{ overdue: number, dueSoon: number }|null|undefined} summary null hides the badge
 * @returns {{ count: number, badgeClass: string|null, label: string }} `badgeClass` is null when hidden
 */
export function navBadgePresentation(summary) {
  const overdue = summary?.overdue ?? 0;
  const dueSoon = summary?.dueSoon ?? 0;
  const count = overdue + dueSoon;
  if (count === 0) return { count: 0, badgeClass: null, label: '' };
  const parts = [];
  if (overdue > 0) parts.push(`${overdue} overdue`);
  if (dueSoon > 0) parts.push(`${dueSoon} due soon`);
  return { count, badgeClass: overdue > 0 ? 'badge-red' : 'badge-amber', label: `${count} to invoice (${parts.join(', ')})` };
}

/**
 * @param {string|null|undefined} url a stored document link
 * @returns {string|null} the link when it is a well-formed https URL the backend would accept, else
 *   null, so nothing else (such as `javascript:`) is ever rendered as a link
 */
export function safeDocumentUrl(url) {
  return typeof url === 'string' && url.length <= MAX_URL_LENGTH && HTTPS_URL.test(url) ? url : null;
}

function boundedText(text, name, label, maxLength, errors, required) {
  const value = (text ?? '').trim();
  if (value === '') {
    if (required) errors[name] = `Enter ${label}.`;
    return null;
  }
  if (value.length > maxLength) errors[name] = `Use at most ${maxLength} characters.`;
  return value;
}

/**
 * Validates the platform cost form the way the backend does and builds the request.
 *
 * @param {{ supplier?: string, label?: string, invoiceNumber?: string, periodFrom?: string, periodTo?: string,
 *           amount?: string, documentUrl?: string }} form raw input texts
 * @returns {{ valid: boolean, errors: Record<string, string>, request: PlatformCostApiRequest|null }}
 */
export function validatePlatformCostForm(form) {
  const errors = {};
  const supplier = boundedText(form.supplier, 'supplier', 'a supplier', MAX_TEXT_LENGTH, errors, true);
  const label = boundedText(form.label, 'label', 'a label', MAX_TEXT_LENGTH, errors, true);
  const invoiceNumber = boundedText(form.invoiceNumber, 'invoiceNumber', 'an invoice number', MAX_INVOICE_NUMBER_LENGTH, errors, false);
  requiredDate(form.periodFrom, 'periodFrom', errors);
  requiredDate(form.periodTo, 'periodTo', errors);
  if (!errors.periodFrom && !errors.periodTo && form.periodTo < form.periodFrom) {
    errors.periodTo = `The period cannot end before ${formatDate(form.periodFrom)}.`;
  }
  const amount = moneyField(form.amount, 'amount', errors, true);
  const documentUrl = blankToNull(form.documentUrl);
  if (documentUrl !== null && safeDocumentUrl(documentUrl) === null) {
    errors.documentUrl = documentUrl.length > MAX_URL_LENGTH
      ? `Use at most ${MAX_URL_LENGTH} characters.`
      : 'Enter a link that starts with https:// and has no spaces.';
  }

  if (Object.keys(errors).length > 0) return { valid: false, errors, request: null };
  return {
    valid: true,
    errors,
    request: { supplier, label, invoiceNumber, periodFrom: form.periodFrom, periodTo: form.periodTo, amount, documentUrl },
  };
}

/**
 * Validates the one-off income form the way the backend does and builds the request.
 *
 * @param {{ label?: string, invoiceNumber?: string, invoicedOn?: string, amount?: string, cost?: string }} form
 *   raw input texts
 * @returns {{ valid: boolean, errors: Record<string, string>, request: OneOffIncomeApiRequest|null }}
 */
export function validateOneOffIncomeForm(form) {
  const errors = {};
  const label = boundedText(form.label, 'label', 'a label', MAX_TEXT_LENGTH, errors, true);
  const invoiceNumber = boundedText(form.invoiceNumber, 'invoiceNumber', 'an invoice number', MAX_INVOICE_NUMBER_LENGTH, errors, true);
  requiredDate(form.invoicedOn, 'invoicedOn', errors);
  const amount = moneyField(form.amount, 'amount', errors, true);
  if (amount === '0.00') errors.amount = 'The amount must be above zero.';
  const cost = moneyField(form.cost, 'cost', errors, false);

  if (Object.keys(errors).length > 0) return { valid: false, errors, request: null };
  return { valid: true, errors, request: { label, invoiceNumber, invoicedOn: form.invoicedOn, amount, cost } };
}

/**
 * @param {string} text the corrected supplier cost as typed; empty means no cost
 * @returns {{ error: string|null, request: InvoiceCostApiRequest|null }}
 */
export function toInvoiceCostRequest(text) {
  const { value, error } = normalizeMoney(text, { required: false });
  return error ? { error, request: null } : { error: null, request: { cost: value } };
}
